import type { paths } from "../types/api.generated";
import * as api from "./client";
import { formatDateForForm, formatDateForApi } from "../utils/dateFormatter";

// ============================================================================
// Type Definitions
// ============================================================================

type APIEdition =
  paths["/api/v1/editions/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["edition"];

// UI type - converts numeric id to string for HierarchicalItem compatibility
export type Edition = Omit<APIEdition, "id"> & {
  id: string;
  [key: string]: unknown;
};

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type EditionListResponse =
  paths["/api/v1/editions"]["get"]["responses"]["200"]["content"]["application/json"];
export type EditionSingleResponse =
  paths["/api/v1/editions/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

// ============================================================================
// Service
// ============================================================================

// Map API edition to UI type (converts numeric id to string)
function mapToEdition(apiEdition: APIEdition): Edition {
  return {
    ...apiEdition,
    id: String(apiEdition.id),
    publishedDate: formatDateForForm(apiEdition.publishedDate),
  };
}

export const editions = {
  getAll: async (directoryId?: string, status?: string): Promise<Edition[]> => {
    const params = new URLSearchParams();
    if (directoryId !== undefined) params.append("directoryId", directoryId);
    if (status) params.append("status", status);

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<EditionListResponse>(`/v1/taxonomy/editions${query}`);
    return response.editions.map(mapToEdition);
  },

  getById: async (id: string): Promise<Edition> => {
    const response = await api.get<EditionSingleResponse>(`/v1/taxonomy/editions/${id}`);
    return mapToEdition(response.edition);
  },

  create: async (data: {
    name: string;
    code: string;
    directoryId: string;
    status: string;
    publishedDate?: string | null;
  }): Promise<Edition> => {
    const response = await api.post<EditionSingleResponse>("/v1/taxonomy/editions", {
      ...data,
      publishedDate: formatDateForApi(data.publishedDate),
    });
    return mapToEdition(response.edition);
  },

  update: async (
    id: string,
    data: {
      name?: string;
      code?: string;
      directoryId?: string;
      status?: string;
      publishedDate?: string | null;
    },
  ): Promise<Edition> => {
    const response = await api.put<EditionSingleResponse>(`/v1/taxonomy/editions/${id}`, {
      ...data,
      publishedDate: formatDateForApi(data.publishedDate),
    });
    return mapToEdition(response.edition);
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/taxonomy/editions/${id}`);
  },
};
